import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { BuyerActivity } from "components/buyer_activity/BuyerActivity";
import SupplierRelationships from "components/buyer_activity/supplier_relationships/SupplierRelationships";
import { generateNoticeSearchUrl } from "components/notices/utils";
import { BreadcrumbContainer, RouterBreadcrumb } from "components/ui/breadcrumb/SimpleBreadcrumb";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { useLead, useLeadSupplierBuyerNoticeSummary } from "lib/hooks/api/leads/useLead";
import { BuyerActivityObjectType, BuyerActivityStatus } from "lib/types/graphQLEnums";

export default function LeadBuyerActivityPage({ leadId }: { leadId: string }) {
  const { data: lead, isLoading, isError } = useLead(leadId);

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this buyer activity report"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!lead) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Lead not found"
          description="The lead you are looking for does not exist."
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: lead.buyers?.map((buyer) => buyer.id) ?? [],
  });

  return (
    <Container>
      <BreadcrumbContainer>
        <RouterBreadcrumb
          overrides={{
            [`/leads/${leadId}`]: { label: lead.name ?? "Lead details" },
            [`/leads/${leadId}/buyer-activity`]: {
              label: `Overview of ${lead.name ?? "this lead"}`,
            },
          }}
        />
      </BreadcrumbContainer>
      <Content>
        <Flex column gap={24}>
          <BuyerActivity
            summary={lead.buyerActivity ?? null}
            notices={lead.notices ?? []}
            objectId={lead.id}
            objectType={BuyerActivityObjectType.Lead}
            noticesSearchUrl={noticesSearchUrl}
            leadName={lead.name}
            buyerName={lead.buyers?.[0]?.name}
            buyerId={lead.buyers?.[0]?.id}
            // A lead can hold several notices; the first is the one it was created from, so that's
            // the one the header links back to.
            noticeId={lead.notices?.[0]?.guid}
            noticeTitle={lead.notices?.[0]?.name}
          />
          <SupplierRelationships
            isLoading={lead.buyerActivity?.status === BuyerActivityStatus.Pending}
            savedNotices={lead.notices}
            similarNotices={lead.buyerActivity?.relatedNotices ?? []}
            incumbentSupplierIds={lead.suppliers.map((supplier) => supplier.id)}
            objectId={lead.id}
            queryProvider={useLeadSupplierBuyerNoticeSummary}
            noticesSearchUrl={noticesSearchUrl}
          />
        </Flex>
      </Content>
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
});

const Content = styled(Flex)({
  flexDirection: "column",
  gap: 48,
  padding: "48px 96px",
});
